import { useCallback, useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';
import { MotionPathPlugin } from 'gsap/dist/MotionPathPlugin';

import { observeElementResize } from '#/lib/observe-element-resize';
import { COLORS } from '#/lib/utils';

import { TranscendLogo } from '../TranscendLogo';
import { AnimatedBorderGenerator } from './AnimationAtoms/AnimatedPathGenerator';
import { AvatarBlock } from './AnimationAtoms/AvatarBlock';
import { CardIconBlock } from './AnimationAtoms/CardIconBlock';
import { InitiativesBlock } from './AnimationAtoms/InitiativesBlock';
import {
  addBorderAnimation,
  iconStackDurations,
  LINE_DRAW_SPEED,
} from './animationUtils';
import { cardIconBlocks } from './content';
import { usePauseTimelineWhenOffscreen } from './usePauseTimelineWhenOffscreen';

gsap.registerPlugin(DrawSVGPlugin, MotionPathPlugin);

export function DesktopAnimation() {
  const timelineRef = useRef<GSAPTimeline>(null);
  const container = useRef<HTMLDivElement>(null);
  const [activeButton, setActiveButton] = useState(0);

  // Pause DrawSVG work while offscreen so nav/CTA clicks aren't competing
  // with an infinite GSAP loop on the main thread.
  const isVisibleRef = usePauseTimelineWhenOffscreen(container, timelineRef);

  // Function to restart the animation
  const restartAnimation = useCallback(() => {
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    const q = gsap.utils.selector(container.current);

    timelineRef.current = gsap
      .timeline({
        repeat: -1,
        // Built paused; played below only if already in view, otherwise the
        // offscreen observer starts it on entry.
        paused: true,
        onRepeat: () => {
          // Functional update only — keep this callback stable so the
          // timeline isn't rebuilt every avatar cycle.
          setActiveButton((current) => (current === 3 ? 0 : current + 1));
        },
      })
      .set(q('.svg-draw'), { drawSVG: '0%' })
      // Outline behind 4 boxes
      .fromTo(
        `#horizontal-line`,
        { drawSVG: '0% 0%' },
        { duration: 0.01, drawSVG: '0% 60%', ease: 'none', delay: 1 },
      )
      .to(
        `#horizontal-line`,
        { drawSVG: '60% 100%', duration: 0.17, ease: 'none' },
        '>',
      )
      .to(
        `#horizontal-line`,
        { drawSVG: '100% 100%', duration: 0.02, ease: 'none' },
        '>',
      )

      // Durations are sized so the leading edge travels at a constant speed
      // across every stretch (matching the entry sweep). The final approach
      // around the far corner was previously ~30% slower than the entry,
      // which read as the left and right sides moving at different speeds.
      .fromTo(
        `#outline-bottom-motion`,
        { drawSVG: '84% 84%' },
        { duration: 0.5, drawSVG: `74% 84%`, ease: 'none' },
        'outline',
      )
      .to(
        `#outline-bottom-motion`,
        { drawSVG: `65% 70%`, duration: 0.45, ease: 'none' },
        '>',
      )
      .to(
        `#outline-bottom-motion`,
        { drawSVG: `50% 55%`, duration: 0.75, ease: 'none', delay: 0.7 },
        '>',
      )
      .to(
        `#outline-bottom-motion`,
        { drawSVG: `37% 47%`, duration: 0.65, ease: 'none', delay: 0.5 },
        '>',
      )
      .to(
        `#outline-bottom-motion`,
        { drawSVG: '37% 37%', duration: 0.4, ease: 'none' },
        '>',
      )

      .fromTo(
        `#outline-top-motion`,
        { drawSVG: '37% 37%' },
        { duration: 0.5, drawSVG: `37% 47%`, ease: 'none' },
        'outline',
      )
      .to(
        `#outline-top-motion`,
        { drawSVG: `50% 55%`, duration: 0.45, ease: 'none' },
        '>',
      )
      .to(
        `#outline-top-motion`,
        { drawSVG: `65% 70%`, duration: 0.75, ease: 'none', delay: 0.7 },
        '>',
      )
      .to(
        `#outline-top-motion`,
        { drawSVG: `74% 84%`, duration: 0.65, ease: 'none', delay: 0.5 },
        '>',
      )
      .to(
        `#outline-top-motion`,
        { duration: 0.4, drawSVG: `84% 84%`, ease: 'none' },
        '>',
      );

    //  card borders above outline
    addBorderAnimation(
      timelineRef.current,
      'cardBorder1',
      'box1',
      {
        ...iconStackDurations,
        tlPosition: 'outline+=0.8',
      },
      q,
    );
    addBorderAnimation(
      timelineRef.current,
      'cardBorder2',
      'box1',
      {
        ...iconStackDurations,
        tlPosition: 'outline+=0.8',
      },
      q,
    );
    addBorderAnimation(
      timelineRef.current,
      'cardBorder3',
      'box2',
      {
        ...iconStackDurations,
        tlPosition: 'outline+=2.2',
      },
      q,
    );
    addBorderAnimation(
      timelineRef.current,
      'cardBorder4',
      'box2',
      {
        ...iconStackDurations,
        tlPosition: 'outline+=2.2',
      },
      q,
    );

    // connector line to initiatives card

    // Phase-matched to the entry line (#horizontal-line) so both horizontal
    // connectors read at the same speed: instant pop-in of the first 48px,
    // leading edge travelling at ~190px/s, then a near-instant collapse.
    // This line is 120px vs the entry's 80px, hence the longer travel.
    timelineRef.current
      .addLabel('drawCurves', '-=0.4')
      .fromTo(
        `#straight-line`,
        { drawSVG: '0% 0%' },
        { duration: 0.01, drawSVG: '0% 40%', ease: 'none' },
        'drawCurves',
      )
      .to(
        `#straight-line`,
        { drawSVG: '60% 100%', duration: 0.42, ease: 'none' },
        'drawCurves+=0.01',
      )
      .to(
        `#straight-line`,
        { drawSVG: '100% 100%', duration: 0.03, ease: 'none' },
        'drawCurves+=0.43',
      );

    // initiatives card border
    addBorderAnimation(
      timelineRef.current,
      'initiativesBlock',
      'btns',
      {
        // Starts as the connector line lands (it finishes at +=0.46)
        tlPosition: 'drawCurves+=0.44',
        finalFadeDuration: 0.15,
        drawDuration: 0.75,
        fadeDuration: 0.15,
        // left-middle of the 280x320 card border path
        startPosition: 85.7,
        linePercentage: 60,
      },
      q,
    );

    timelineRef.current.timeScale(LINE_DRAW_SPEED);
    if (isVisibleRef.current) timelineRef.current.play();
  }, [isVisibleRef]);

  // Robust resize listener with threshold and debouncing
  useEffect(() => {
    if (!container.current) return;

    let lastWidth = container.current.offsetWidth;
    let timeoutId: NodeJS.Timeout;

    const disconnect = observeElementResize(container.current, (entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        const widthDifference = Math.abs(width - lastWidth);

        // Only restart if width changed by more than 10px
        if (widthDifference > 10) {
          lastWidth = width;

          // Debounce to prevent multiple rapid fires
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            restartAnimation();
          }, 100);
        }
      }
    });

    return () => {
      disconnect();
      clearTimeout(timeoutId);
    };
  }, [restartAnimation]);

  useGSAP(
    () => {
      // Wait a bit for all components to render
      gsap.delayedCall(0.2, () => {
        restartAnimation();
      });

      return () => {
        timelineRef.current?.kill();
      };
    },
    { scope: container },
  );

  return (
    <div ref={container} className="flex flex-row items-center justify-center">
      <AvatarBlock activeButton={activeButton} />
      <div className="flex flex-row items-center">
        <svg
          width="80"
          height="2"
          viewBox="0 0 80 2"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 1H80" stroke={COLORS.grayscale['03']} />
          <path
            d="M0 1H80"
            className="stroke-electric-05 svg-draw"
            id="horizontal-line"
            strokeWidth={2}
          />
        </svg>
        <div className="grid-stack">
          <div className="flex items-center">
            <div className="bg-charcoal rounded-full p-15 max-w-max max-h-max z-2">
              <TranscendLogo className="w-55" iconOnly />
            </div>
          </div>
          <div className="flex items-center relative">
            <div className="ml-40 w-full h-[calc(100%-190px)] relative">
              <div className="absolute top-0 left-0 w-full h-full rounded-60" />
              <AnimatedBorderGenerator
                topMotionId="outline-top-motion"
                bottomMotionId="outline-bottom-motion"
                borderRadius={60}
                strokeWidth={1}
                strokeColor={COLORS.grayscale['03']}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-40 xl:gap-70 h-full relative px-40 xl:px-68 ml-43">
            <div className="flex flex-col justify-between items-center z-2">
              <CardIconBlock {...cardIconBlocks.ai} />
              <CardIconBlock {...cardIconBlocks.dataWarehouses} />
            </div>
            <div className="flex flex-col justify-between items-center gap-110 z-2">
              <CardIconBlock {...cardIconBlocks.marketingAdTech} />
              <CardIconBlock {...cardIconBlocks.engagementPlatforms} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-120 items-center self-stretch">
        <svg
          width="120"
          height="2"
          viewBox="0 0 120 2"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="w-full"
        >
          <path d="M0 1H120" stroke={COLORS.grayscale['03']} />
          <path
            d="M0 1H120"
            className="stroke-electric-04 svg-draw"
            id="straight-line"
            strokeWidth={2}
          />
        </svg>
      </div>
      <div className="flex items-center py-20">
        <InitiativesBlock iconBlockId="initiativesBlock" />
      </div>
    </div>
  );
}
